import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "../../data/course";

const LINKS = [
  { label: "Overview", href: "#overview" },
  { label: "Curriculum", href: "#curriculum" },
  { label: "Tools", href: "#tools" },
  { label: "Projects", href: "#projects" },
  { label: "Placement", href: "#placement" },
  { label: "Certificates", href: "#certifications" },
  { label: "Fees", href: "#fees" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#EEF1F8]/75 backdrop-blur-xl border-b border-[#293891]/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-12">
        <a href="#top" data-testid="nav-logo" className="flex items-center">
          <img src={LOGO_URL} alt="ExcellenC" className="h-7 w-auto md:h-8" />
        </a>

        <div className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="group relative text-sm font-medium text-[#3A4152] transition-colors hover:text-[#293891]"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#e63329] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href="#fees"
            data-testid="nav-enroll-cta"
            className="hidden items-center gap-2 rounded-full bg-[#E63329] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_-10px_rgba(230,51,41,0.6)] transition-transform duration-300 hover:scale-[1.04] lg:inline-flex"
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
            Enroll Now
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#293891]/15 text-[#293891] lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-[#293891]/10 bg-[#EEF1F8]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="rounded-lg px-3 py-3 text-base font-medium text-[#3A4152] transition-colors hover:bg-[#293891]/[0.03] hover:text-[#293891]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#fees"
                onClick={() => setOpen(false)}
                data-testid="mobile-fees-link"
                className="mt-2 rounded-full bg-[#E63329] px-6 py-3 text-center text-sm font-semibold text-white"
              >
                Enroll Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
